"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Money03Icon, UserGroupIcon, Briefcase01Icon, Invoice01Icon, ArrowUp01Icon, ArrowDown01Icon, ArrowRight01Icon, Calendar01Icon, Wallet01Icon, PlusSignIcon,
} from "@hugeicons/core-free-icons";
import { useNavigate } from "react-router-dom";
import { get } from "../../utils/service";
import { formatMoney, isAdminUser } from "@/utils/finance";

interface DashboardData {
  stats: { total_leads: number; total_projects: number; total_revenue: number; qualified_leads?: number; active_projects?: number; completed_projects?: number; currency?: string };
  quick_stats: { total_pipeline_value: number; active_leads: number; active_projects: number; won_this_month: number; currency?: string };
  sales_by_month: Array<{ month: string; sales: number; leads: number; conversions: number }>;
  revenue_by_type: Array<{ type: string; revenue: number; count: number }>;
  conversion_rate: { overall_rate: number; total_leads: number; converted: number };
}
interface InvoiceStats { display: { currency: string; outstanding: string } | null; by_currency: Array<{ currency: string; outstanding: string; overdue_count: number; unpaid_count: number }> }

const compact = (n: number, currency?: string) => {
  const v = Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
  return currency ? `${currency} ${v}` : v;
};

const pct = (cur: number, prev: number) => (prev > 0 ? ((cur - prev) / prev) * 100 : cur > 0 ? 100 : 0);

const Delta = ({ value, onDark }: { value: number; onDark?: boolean }) => {
  const up = value >= 0;
  const tone = onDark ? "bg-white/25 text-white" : up ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600";

  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      <HugeiconsIcon icon={up ? ArrowUp01Icon : ArrowDown01Icon} size={11} />{Math.abs(value).toFixed(1)}%
    </span>
  );
};

const Card = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <div className={`rounded-3xl border border-gray-200 bg-white p-5 ${className}`}>{children}</div>
);

function StatCard({ icon, label, value, caption, delta, primary }: { icon: typeof Money03Icon; label: string; value: string; caption: string; delta?: number; primary?: boolean }) {
  return (
    <div className={`rounded-3xl p-5 ${primary ? "border border-primary bg-gradient-to-br from-primary to-[#ff9a5c] text-white" : "border border-gray-200 bg-white"}`}>
      <div className="flex items-start justify-between">
        <span className={`flex h-12 w-12 items-center justify-center rounded-full ${primary ? "bg-white text-primary" : "bg-gray-100 text-gray-700"}`}><HugeiconsIcon icon={icon} size={22} /></span>
        {delta !== undefined && <Delta value={delta} onDark={primary} />}
      </div>
      <p className={`mt-4 text-sm ${primary ? "text-white/90" : "text-gray-600"}`}>{label}</p>
      <div className="mt-1 flex flex-wrap items-end gap-x-3">
        <p className={`text-3xl font-bold tracking-tight ${primary ? "text-white" : "text-gray-900"}`}>{value}</p>
        <p className={`pb-1 text-xs leading-tight max-w-[8rem] ${primary ? "text-white/80" : "text-gray-500"}`}>{caption}</p>
      </div>
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ payload: { leads: number; conversions: number; sales: number } }>; label?: string }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;

  return (
    <div className="rounded-2xl bg-gray-900 px-4 py-3 text-xs text-white shadow-xl">
      <p className="mb-1.5 font-semibold">{label}</p>
      <p className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-gray-300" />{d.leads} <span className="text-gray-400">leads</span></p>
      <p className="mt-1 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-primary" />{d.conversions} <span className="text-gray-400">converted</span></p>
      <p className="mt-1 text-gray-400">Sales {compact(d.sales)}</p>
    </div>
  );
};

export default function DashboardOverview({ data, loading }: { data: DashboardData | null; loading: boolean }) {
  const navigate = useNavigate();
  const [inv, setInv] = useState<InvoiceStats | null>(null);
  const actions = [
    { icon: PlusSignIcon, title: "Add new lead", description: "Capture a new business opportunity", path: "/sales/leads" },
    { icon: Briefcase01Icon, title: "View projects", description: "Monitor the project portfolio", path: "/projects/all" },
    { icon: Invoice01Icon, title: "Invoices", description: "Bill clients and generate backdated invoices", path: "/invoices" },
    { icon: Wallet01Icon, title: "New expense", description: "Claim money you paid for the company", path: "/expenses/new" },
    { icon: Calendar01Icon, title: "Calendar", description: "Meetings and follow-ups", path: "/calendar" },
    ...(isAdminUser() ? [{ icon: UserGroupIcon, title: "Manage team", description: "View users and access permissions", path: "/users" }] : []),
  ];

  useEffect(() => {
    get<{ data: InvoiceStats }>("/invoices/stats").then((r) => setInv(r.data)).catch(() => setInv(null));
  }, []);

  const view = useMemo(() => {
    if (!data) return null;
    const m = data.sales_by_month;
    const last = m[m.length - 1], prev = m[m.length - 2];
    const currency = data.stats.currency || data.quick_stats.currency || "USD";
    const types = [...data.revenue_by_type].sort((a, b) => b.revenue - a.revenue).slice(0, 4);
    const typeTotal = types.reduce((n, t) => n + t.revenue, 0) || 1;
    const total = Math.max(data.stats.total_projects, 1);

    return {
      currency,
      revenueDelta: pct(last?.sales ?? 0, prev?.sales ?? 0),
      leadsDelta: pct(last?.leads ?? 0, prev?.leads ?? 0),
      types: types.map((t) => ({ ...t, share: (t.revenue / typeTotal) * 100 })),
      rings: [
        { name: "Active projects", value: ((data.stats.active_projects ?? data.quick_stats.active_projects) / total) * 100, fill: "#ff6d27" },
        { name: "Completed", value: ((data.stats.completed_projects ?? 0) / total) * 100, fill: "#ef4444" },
        { name: "Qualified leads", value: (((data.stats.qualified_leads ?? 0) / Math.max(data.stats.total_leads, 1)) * 100), fill: "#d1d5db" },
      ],
    };
  }, [data]);

  if (loading || !data || !view) {
    return (
      <div className="grid gap-5 xl:grid-cols-3 animate-pulse">
        <div className="xl:col-span-2 grid gap-5"><div className="grid grid-cols-2 gap-5">{[0, 1, 2, 3].map((i) => <div key={i} className="h-40 rounded-3xl bg-gray-100" />)}</div><div className="h-80 rounded-3xl bg-gray-100" /></div>
        <div className="grid gap-5"><div className="h-96 rounded-3xl bg-gray-100" /><div className="h-64 rounded-3xl bg-gray-100" /></div>
      </div>
    );
  }

  const invoiceOutstanding = inv?.display
    ? formatMoney(inv.display.outstanding, inv.display.currency)
    : inv?.by_currency[0] ? formatMoney(inv.by_currency[0].outstanding, inv.by_currency[0].currency) : "—";
  const unpaid = inv?.by_currency.reduce((n, c) => n + c.unpaid_count, 0) ?? 0;

  return (
    <div className="grid items-stretch gap-5 xl:grid-cols-3">
      {/* Row 1: the four cards, and the pipeline card at exactly their height */}
      <div className="xl:col-span-2 grid">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <StatCard primary icon={Money03Icon} label="Total revenue" value={compact(data.stats.total_revenue, view.currency)} caption="Payments received vs last month" delta={view.revenueDelta} />
        <StatCard icon={UserGroupIcon} label="Total leads" value={compact(data.stats.total_leads)} caption={`${data.quick_stats.active_leads} active in pipeline`} delta={view.leadsDelta} />
        <StatCard icon={Briefcase01Icon} label="Active projects" value={compact(data.quick_stats.active_projects)} caption={`of ${data.stats.total_projects} projects`} delta={pct(data.quick_stats.won_this_month, 0)} />
        <StatCard icon={Invoice01Icon} label="Outstanding invoices" value={invoiceOutstanding} caption={`${unpaid} unpaid invoice${unpaid === 1 ? "" : "s"}`} />
      </div>
      </div>
      <Card className="flex flex-col">
        <div className="flex items-start justify-between"><div><h3 className="text-xl font-semibold text-gray-900">Pipeline statistic</h3><p className="text-sm text-gray-500">Where your work stands</p></div></div>
        <div className="mt-2 flex items-center gap-4">
          <div className="h-56 w-56 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="30%" outerRadius="100%" data={view.rings} startAngle={90} endAngle={-270} barSize={14}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar dataKey="value" cornerRadius={20} background={{ fill: "#f3f4f6" }} />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
          <div className="min-w-0">
            <p className="text-3xl font-bold tracking-tight text-gray-900">{compact(data.quick_stats.total_pipeline_value)}</p>
            <p className="text-sm text-gray-500">Pipeline value</p>
            <span className="mt-2 inline-block rounded-full bg-green-100 px-2.5 py-0.5 text-[11px] font-semibold text-green-700">{data.conversion_rate.overall_rate.toFixed(1)}%</span>
          </div>
        </div>
        <ul className="mt-4 space-y-3">
          {view.rings.map((r, i) => (
            <li key={r.name} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-3 text-gray-800"><span className="h-3 w-3 rounded-full" style={{ background: r.fill }} />{r.name}</span>
              <span className="flex items-center gap-2"><span className="font-semibold text-gray-900">{[data.stats.active_projects ?? data.quick_stats.active_projects, data.stats.completed_projects ?? 0, data.stats.qualified_leads ?? 0][i]}</span><span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{r.value.toFixed(0)}%</span></span>
            </li>
          ))}
        </ul>
      </Card>
      {/* Row 2: sales chart, and quick actions at exactly its height */}
      <Card className="xl:col-span-2 flex flex-col">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><h3 className="text-xl font-semibold text-gray-900">Sales habits</h3><p className="text-sm text-gray-500">Track your leads against conversions</p></div>
          <span className="rounded-full bg-gray-100 px-4 py-1.5 text-xs text-gray-700">Last {data.sales_by_month.length} months</span>
        </div>
        <div className="mt-3 flex gap-5 text-sm text-gray-600">
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-gray-300" />Leads</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-primary" />Conversions</span>
        </div>
        <div className="mt-4 min-h-[15rem] flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.sales_by_month} barGap={6} barCategoryGap="28%">
              <CartesianGrid vertical={false} strokeDasharray="4 6" stroke="#e5e7eb" />
              <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fill: "#6b7280", fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} allowDecimals={false} tick={{ fill: "#6b7280", fontSize: 12 }} width={32} />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} content={<ChartTooltip />} />
              <Bar dataKey="leads" fill="#d1d5db" radius={20} maxBarSize={24} />
              <Bar dataKey="conversions" fill="#ff6d27" radius={20} maxBarSize={24}>
                {data.sales_by_month.map((_, i) => <Cell key={i} fill="#ff6d27" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card className="flex flex-col">
        <div><h3 className="text-xl font-semibold text-gray-900">Quick actions</h3><p className="text-sm text-gray-500">Frequently used tasks</p></div>
        <ul className="mt-2 min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto">
          {actions.map((a) => (
            <li key={a.title}>
              <button onClick={() => navigate(a.path)} className="group flex w-full items-center gap-3 py-2.5 text-left">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700 transition group-hover:bg-primary group-hover:text-white"><HugeiconsIcon icon={a.icon} size={19} /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-medium text-gray-900">{a.title}</span><span className="block truncate text-xs text-gray-500">{a.description}</span></span>
                <HugeiconsIcon icon={ArrowRight01Icon} size={16} className="text-gray-300 transition group-hover:text-primary" />
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
